import { enTetesAuth } from './enTetesAuth';
/**
 * Messages Service - Handle messaging functionality
 */


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://apicabaza.creago.be/rest/cabaza';
const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://cabaza.creago.be/files';

/**
 * Handle API response
 */
const handleResponse = async (response) => {
    const text = await response.text();
    let result;
    try {
        result = text ? JSON.parse(text) : {};
    } catch (e) {
        console.error('Failed to parse messages response:', e);
        throw new Error('Invalid response from server');
    }

    if (!response.ok) {
        throw new Error(result.message || result.MESSAGE || `HTTP ${response.status}`);
    }

    return result;
};

/**
 * Get userId from localStorage
 */
const getUserId = () => localStorage.getItem('visitorPeopleId') || localStorage.getItem('userId') || '';

/**
 * Get all conversations for the current user
 * @param {string} search - Optional search keyword
 * @returns {Promise<Array>} List of conversations
 */
export const getConversations = async (search = '') => {
    try {
        const userId = getUserId();
        const params = new URLSearchParams({ userId });
        if (search) params.append('search', search);

        const response = await fetch(`${API_BASE_URL}/messages/conversations?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const conversations = result.data || result.DATA || [];
        return conversations;
    } catch (error) {
        console.error('Error getting conversations:', error);
        return [];
    }
};

/**
 * Get messages for a conversation
 * @param {number} conversationId - Conversation ID
 * @param {number} beforeId - Optional message ID for pagination
 * @param {number} limit - Number of messages to fetch
 * @returns {Promise<Array>} List of messages
 */
export const getMessages = async (conversationId, beforeId = 0, limit = 50) => {
    try {
        const userId = getUserId();
        const params = new URLSearchParams({ userId, limit: limit.toString() });
        if (beforeId > 0) params.append('beforeId', beforeId.toString());

        const response = await fetch(`${API_BASE_URL}/messages/conversations/${conversationId}?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const messages = result.data || result.DATA || [];

        // Build full URLs for attachments using file path
        // FILES_BASE_URL already contains /files (e.g., https://cabaza.creago.be/files)
        return messages.map(msg => ({
            ...msg,
            attachments: (msg.attachments || msg.ATTACHMENTS || []).map(att => {
                let filePath = att.filePath || att.FILEPATH || att.file_path || '';
                let thumbnailPath = att.thumbnailPath || att.THUMBNAILPATH || att.thumbnail_path || '';
                // Remove leading slash to avoid double slash
                if (filePath.startsWith('/')) filePath = filePath.substring(1);
                if (thumbnailPath.startsWith('/')) thumbnailPath = thumbnailPath.substring(1);
                return {
                    ...att,
                    fullUrl: filePath ? `${FILES_BASE_URL}/${filePath}` : null,
                    thumbnailUrl: thumbnailPath ? `${FILES_BASE_URL}/${thumbnailPath}` : null
                };
            })
        }));
    } catch (error) {
        console.error('Error getting messages:', error);
        return [];
    }
};

/**
 * Create a new conversation
 * @param {string} participantIds - Comma-separated participant IDs
 * @param {string} name - Optional group name
 * @param {boolean} isGroup - Is this a group chat
 * @param {string} groupIds - Comma-separated group IDs (optional)
 * @returns {Promise<Object>} Result with conversationId
 */
export const createConversation = async (participantIds, name = '', isGroup = false, groupIds = '') => {
    try {
        const userId = getUserId();
        const body = { userId, name, isGroup };

        if (participantIds) body.participantIds = participantIds;
        if (groupIds) body.groupIds = groupIds;

        const response = await fetch(`${API_BASE_URL}/messages/conversations`, {
            method: 'POST',
            headers: enTetesAuth(false),
            credentials: 'include',
            headers: enTetesAuth(),
            body: JSON.stringify(body)
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || {};
        return {
            success: result.success || result.SUCCESS,
            conversationId: data.conversationId || data.CONVERSATIONID,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error creating conversation:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Search for participants and groups
 * Returns contacts, public peoples, and user's groups
 * @param {string} keyword - Search keyword (min 2 chars)
 * @returns {Promise<Array>} List of results with type field (contact, people, group)
 */
export const searchParticipantsAndGroups = async (keyword) => {
    try {
        if (!keyword || keyword.length < 2) {
            return [];
        }

        const userId = getUserId();
        const params = new URLSearchParams({ userId, keyword });

        const response = await fetch(`${API_BASE_URL}/messages/search-participants?${params}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || [];

        // Normalize the data
        return data.map(item => ({
            id: item.id || item.ID,
            type: item.type || item.TYPE,
            firstName: item.firstName || item.FIRSTNAME,
            lastName: item.lastName || item.LASTNAME,
            email: item.email || item.EMAIL,
            groupId: item.groupId || item.GROUPID,
            groupUuid: item.groupUuid || item.GROUPUUID,
            groupName: item.groupName || item.GROUPNAME,
            memberCount: item.memberCount || item.MEMBERCOUNT || 0
        }));
    } catch (error) {
        console.error('Error searching participants:', error);
        return [];
    }
};

/**
 * Add a people to the user's Messagerie group
 * Used when messaging a public people who is not yet a contact
 * @param {number} peopleId - People ID to add
 * @returns {Promise<Object>} Result with contactId
 */
export const addToMessagingGroup = async (peopleId) => {
    try {
        const userId = getUserId();
        const response = await fetch(`${API_BASE_URL}/messages/add-to-messaging-group`, {
            method: 'POST',
            headers: enTetesAuth(false),
            credentials: 'include',
            headers: enTetesAuth(),
            body: JSON.stringify({ userId, peopleId })
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || {};
        return {
            success: result.success || result.SUCCESS,
            contactId: data.contactId || data.CONTACTID,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error adding to messaging group:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Find or create a direct conversation with another user
 * @param {number} otherUserId - The other user's ID
 * @returns {Promise<Object>} Result with conversationId and isNew flag
 */
export const findOrCreateDirectConversation = async (otherUserId) => {
    try {
        const userId = getUserId();
        const response = await fetch(`${API_BASE_URL}/messages/direct`, {
            method: 'POST',
            headers: enTetesAuth(false),
            credentials: 'include',
            headers: enTetesAuth(),
            body: JSON.stringify({ userId, otherUserId })
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || {};
        return {
            success: result.success || result.SUCCESS,
            conversationId: data.conversationId || data.CONVERSATIONID,
            isNew: data.isNew || data.ISNEW,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error finding/creating conversation:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Send a message
 * @param {number} conversationId - Conversation ID
 * @param {string} content - Message content
 * @param {string} messageType - Message type (text, image, file)
 * @param {number} replyToId - Optional message ID being replied to
 * @returns {Promise<Object>} Result with messageId
 */
export const sendMessage = async (conversationId, content, messageType = 'text', replyToId = null) => {
    try {
        const userId = getUserId();
        const body = { userId, conversationId, content, messageType };
        if (replyToId) body.replyToId = replyToId;

        const response = await fetch(`${API_BASE_URL}/messages`, {
            method: 'POST',
            headers: enTetesAuth(false),
            credentials: 'include',
            headers: enTetesAuth(),
            body: JSON.stringify(body)
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || {};
        return {
            success: result.success || result.SUCCESS,
            messageId: data.messageId || data.MESSAGEID,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error sending message:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Upload a file for messaging
 * @param {File} file - File to upload
 * @returns {Promise<Object>} Result with file info
 */
export const uploadFile = async (file) => {
    try {
        const userId = getUserId();
        const formData = new FormData();
        formData.append('file', file);

        // enTetesAuth(false) N'AJOUTE PAS Content-Type : le navigateur doit
        // le poser lui-même pour un multipart, avec sa frontière. Seul
        // Authorization nous intéresse ici.
        const headers = enTetesAuth(false);

        const response = await fetch(`${API_BASE_URL}/messages/upload?userId=${userId}`, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: formData
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || {};
        const filePath = data.filePath || data.FILEPATH;

        return {
            success: result.success || result.SUCCESS,
            filePath,
            fullUrl: filePath ? `${FILES_BASE_URL}${filePath}` : null,
            fileName: data.fileName || data.FILENAME,
            fileType: data.fileType || data.FILETYPE,
            fileSize: data.fileSize || data.FILESIZE,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error uploading file:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Add attachment to a message
 * @param {number} messageId - Message ID
 * @param {Object} attachmentData - Attachment info
 * @returns {Promise<Object>} Result
 */
export const addAttachment = async (messageId, attachmentData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/messages/${messageId}/attachment`, {
            method: 'POST',
            headers: enTetesAuth(false),
            credentials: 'include',
            headers: enTetesAuth(),
            body: JSON.stringify(attachmentData)
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || {};
        return {
            success: result.success || result.SUCCESS,
            attachmentId: data.attachmentId || data.ATTACHMENTID,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error adding attachment:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Mark a conversation as read
 * @param {number} conversationId - Conversation ID
 * @returns {Promise<Object>} Result
 */
export const markAsRead = async (conversationId) => {
    try {
        const userId = getUserId();
        const response = await fetch(`${API_BASE_URL}/messages/conversations/${conversationId}/read?userId=${userId}`, {
            method: 'PUT',
            headers: enTetesAuth(false),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return {
            success: result.success || result.SUCCESS,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error marking as read:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a message
 * @param {number} messageId - Message ID
 * @returns {Promise<Object>} Result
 */
export const deleteMessage = async (messageId) => {
    try {
        const userId = getUserId();
        const response = await fetch(`${API_BASE_URL}/messages/${messageId}?userId=${userId}`, {
            method: 'DELETE',
            headers: enTetesAuth(false),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return {
            success: result.success || result.SUCCESS,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error deleting message:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Delete a conversation
 * @param {string} conversationUuid - Conversation UUID
 * @returns {Promise<Object>} Result
 */
export const deleteConversation = async (conversationUuid) => {
    try {
        const userId = getUserId();
        const response = await fetch(`${API_BASE_URL}/messages/conversations/${conversationUuid}?userId=${userId}`, {
            method: 'DELETE',
            headers: enTetesAuth(false),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return {
            success: result.success || result.SUCCESS,
            message: result.message || result.MESSAGE
        };
    } catch (error) {
        console.error('Error deleting conversation:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Get total unread message count
 * @returns {Promise<number>} Unread count
 */
export const getUnreadCount = async () => {
    try {
        const userId = getUserId();
        const response = await fetch(`${API_BASE_URL}/messages/unread-count?userId=${userId}`, {
            method: 'GET',
            credentials: 'include'
        });

        const result = await handleResponse(response);
        const data = result.data || result.DATA || {};
        return data.count || data.COUNT || 0;
    } catch (error) {
        console.error('Error getting unread count:', error);
        return 0;
    }
};

/**
 * Send a message with attachment (combines upload + send + attach)
 * @param {number} conversationId - Conversation ID
 * @param {string} content - Optional text content
 * @param {File} file - File to attach
 * @param {number} replyToId - Optional message ID being replied to
 * @returns {Promise<Object>} Result
 */
export const sendMessageWithAttachment = async (conversationId, content, file, replyToId = null) => {
    try {
        // First upload the file
        const uploadResult = await uploadFile(file);
        if (!uploadResult.success) {
            return { success: false, message: uploadResult.message || 'Failed to upload file' };
        }

        // Determine message type based on file type
        const isImage = uploadResult.fileType?.startsWith('image/');
        const messageType = isImage ? 'image' : 'file';

        // Send the message
        const sendResult = await sendMessage(conversationId, content || '', messageType, replyToId);
        if (!sendResult.success) {
            return { success: false, message: sendResult.message || 'Failed to send message' };
        }

        // Add the attachment to the message
        const attachResult = await addAttachment(sendResult.messageId, {
            fileName: uploadResult.fileName,
            filePath: uploadResult.filePath,
            fileType: uploadResult.fileType,
            fileSize: uploadResult.fileSize
        });

        if (!attachResult.success) {
            console.warn('Message sent but attachment failed:', attachResult.message);
        }

        return {
            success: true,
            messageId: sendResult.messageId,
            attachmentId: attachResult.attachmentId,
            fileUrl: uploadResult.fullUrl
        };
    } catch (error) {
        console.error('Error sending message with attachment:', error);
        return { success: false, message: error.message };
    }
};
